import { useState } from "react";
import { useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { useDebouncedValue } from "@tellescope/react-hooks";
import { buildExistingFilesMdbFilter } from "./buildExistingFilesMdbFilter";

export interface UseExistingFilePickerArgs {
  enduserId: string;
  excludedSecureNames?: string[];
  validFileTypes?: string[];
  multiple: boolean;
  enableQuery: boolean;
}

export const useExistingFilePicker = ({
  enduserId,
  excludedSecureNames,
  validFileTypes,
  multiple,
  enableQuery,
}: UseExistingFilePickerArgs) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery);
  const [selectedSecureNames, setSelectedSecureNames] = useState<string[]>([]);

  const filesQuery = useTellescopeInfiniteQuery({
    model: "files",
    method: "getSome",
    request: {
      limit: 50,
      search: debouncedSearchQuery ? { query: debouncedSearchQuery } : undefined,
      mdbFilter: buildExistingFilesMdbFilter({ enduserId, excludedSecureNames, validFileTypes }),
    },
    enabled: enableQuery && !!enduserId,
    select: (data) => data.pages.flat(),
    placeholderData: (prev) => prev,
  });

  const files = filesQuery.data;

  // Selection order, not page order, decides the order files are appended to the answer.
  const selectedFiles = selectedSecureNames.reduce<NonNullable<typeof files>>((acc, secureName) => {
    const file = files?.find((f) => f.secureName === secureName);
    if (file) acc.push(file);
    return acc;
  }, []);

  const toggleSelected = (secureName: string) =>
    setSelectedSecureNames((prev) => {
      if (prev.includes(secureName)) return prev.filter((s) => s !== secureName);
      return multiple ? [...prev, secureName] : [secureName];
    });

  return {
    searchQuery,
    setSearchQuery,
    isSearchPending: searchQuery !== debouncedSearchQuery,
    files,
    isLoading: filesQuery.isLoading,
    isError: filesQuery.isError,
    fetchNextPage: filesQuery.fetchNextPage,
    selectedFiles,
    selectedSecureNames,
    toggleSelected,
  };
};
